import {
  canTrackOriginalRecordDownload,
  type labRecordType,
  ProcedureRecordType,
  SourceType,
  UnifiedAdditionalRecordClassificationType,
  UnifiedLabRecordType,
  UnifiedProcedureRecordType,
} from '@/feature/patients/types';
import { type DocumentKey, DocumentSource, DocumentType } from './types';

type MarkDownloadedInput =
  | { patientId: string; id: string; kind: 'provider_facility_note' }
  | { patientId: string; id: string; kind: 'imaging' }
  | { patientId: string; id: string; kind: 'pathology' }
  | { patientId: string; id: string; kind: 'medication' }
  | { patientId: string; id: string; kind: 'vital' }
  | { patientId: string; id: string; kind: 'problem' }
  | {
      patientId: string;
      id: string;
      kind: 'lab';
      recordType?: UnifiedLabRecordType;
    }
  | {
      patientId: string;
      id: string;
      kind: 'procedure';
      recordType?: UnifiedProcedureRecordType;
    }
  | {
      patientId: string;
      id: string;
      kind: 'additional';
      classificationType: UnifiedAdditionalRecordClassificationType;
    }
  | {
      patientId: string;
      id: string;
      kind: 'original_record';
      sourceType: SourceType;
    };

const labRecordTypeMap: Record<number, UnifiedLabRecordType> = {
  0: UnifiedLabRecordType.UNSPECIFIED,
  1: UnifiedLabRecordType.LEGACY_LAB,
  2: UnifiedLabRecordType.PREVIOUS_PROVIDER,
};

const mapLabRecordType = (
  rt: labRecordType | undefined,
): UnifiedLabRecordType | undefined => {
  if (rt === undefined) return undefined;
  return labRecordTypeMap[rt as number];
};

const procedureRecordTypeMap: Record<string, UnifiedProcedureRecordType> = {
  [ProcedureRecordType.UNSPECIFIED]: UnifiedProcedureRecordType.UNSPECIFIED,
  [ProcedureRecordType.PROCEDURE_NOTE]:
    UnifiedProcedureRecordType.PROCEDURE_NOTE,
  [ProcedureRecordType.PROCEDURE]: UnifiedProcedureRecordType.PROCEDURE,
};

const mapProcedureRecordType = (
  rt: ProcedureRecordType | undefined,
): UnifiedProcedureRecordType | undefined => {
  if (rt === undefined) return undefined;
  return procedureRecordTypeMap[rt];
};

export const mapDocumentKeyToMarkDownloadedInput = (
  key: DocumentKey | undefined,
  patientId: string | undefined,
): MarkDownloadedInput | null => {
  if (!key || !patientId) return null;
  if (key.source !== DocumentSource.MEDICAL_RECORDS) return null;

  switch (key.type) {
    case DocumentType.PROVIDER_NOTES:
      return {
        patientId,
        id: key.providerNoteId,
        kind: 'provider_facility_note',
      };
    case DocumentType.IMAGING:
      return { patientId, id: key.imagingId, kind: 'imaging' };
    case DocumentType.PATHOLOGY_RESULTS:
      return { patientId, id: key.pathologyId, kind: 'pathology' };
    case DocumentType.MEDICATIONS:
      return key.medicationId
        ? { patientId, id: key.medicationId, kind: 'medication' }
        : null;
    case DocumentType.VITALS:
      return { patientId, id: key.vitalId, kind: 'vital' };
    case DocumentType.PROBLEMS:
      return { patientId, id: key.problemId, kind: 'problem' };
    case DocumentType.LAB_RESULTS:
      return {
        patientId,
        id: key.labId,
        kind: 'lab',
        recordType: mapLabRecordType(key.recordType),
      };
    case DocumentType.PROCEDURES:
      return key.procedureId
        ? {
            patientId,
            id: key.procedureId,
            kind: 'procedure',
            recordType: mapProcedureRecordType(key.recordType),
          }
        : null;
    case DocumentType.ADDITIONAL:
      return {
        patientId,
        id: key.additionalRecordId,
        kind: 'additional',
        classificationType: key.classificationType,
      };
    case DocumentType.ORIGINAL_RECORDS:
      if (!canTrackOriginalRecordDownload(key.sourceType)) {
        return null;
      }

      return {
        patientId,
        id: key.originalRecordId,
        kind: 'original_record',
        sourceType: key.sourceType,
      };
    default:
      return null;
  }
};
